import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import { KEYS } from "platejs";
import { useEditorRef, useEditorSelector } from "platejs/react";
import { memo, useCallback, useState } from "react";
import { RedoIcon } from "../../arbiter-components/icon/redo-icon";
import { cn } from "../../tailwind-utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { EDITOR_OVERLAY_Z_CLASS } from "./editor-overlay";
import { useToolbarOverflow } from "./toolbar-overflow";

const FONT_SIZES = [
  "10px",
  "12px",
  "14px",
  "16px",
  "18px",
  "20px",
  "24px",
  "30px",
  "36px",
] as const;

export const FontSizeToolbarButton = memo(function FontSizeToolbarButton() {
  const editor = useEditorRef();
  const [open, setOpen] = useState(false);
  const { isOverflow } = useToolbarOverflow();

  const currentSize = useEditorSelector((editor) => {
    const value = editor.api.mark(KEYS.fontSize);
    return typeof value === "string" ? value : "";
  }, []);

  const displaySize = currentSize || "14px";

  const handleSelect = useCallback(
    (value: string) => {
      editor.tf.addMarks({ [KEYS.fontSize]: value });
      editor.tf.focus();
      setOpen(false);
    },
    [editor],
  );

  if (isOverflow) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger inset>Font size</DropdownMenuSubTrigger>
        <DropdownMenuSubContent className={EDITOR_OVERLAY_Z_CLASS}>
          {FONT_SIZES.map((size) => (
            <DropdownMenuItem key={size} onSelect={() => handleSelect(size)}>
              {size}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <DropdownMenu modal={false} onOpenChange={setOpen} open={open}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "inline-flex h-6 items-center gap-1 rounded-sm px-2 text-xs text-foreground",
            "hover:bg-accent hover:text-accent-foreground",
          )}
          type="button"
        >
          <span>{displaySize}</span>
          <RedoIcon Icon={ChevronDownIcon} size={12} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={cn("min-w-0 w-20", EDITOR_OVERLAY_Z_CLASS)}
      >
        {FONT_SIZES.map((size) => (
          <DropdownMenuItem key={size} onSelect={() => handleSelect(size)}>
            <span className="text-sm">{size}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
